/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : DynamicFormDialog.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 20-07-2024
 * @description : Custom dynamic form component.
 */
import {
  Box,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";

import React, { useEffect, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import UpdateIcon from "@mui/icons-material/Update";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";




import DynamicFormTemplate from "./DynamicFormTemplate";
import {
  ALIGN_TYPES,
  EDIT_TYPES,
  FIELD_TYPES,
  FORM_TYPES,
} from "../../config/appConstants";
import { fontSizeDetails } from "../../config/appTheme";
import CustomFormTextFieldV2 from "../customComponentsV2/common/textField/CustomFormTextFieldV2";
import CustomButtonV2 from "../customComponentsV2/common/button/CustomButtonV2";
import CustomRadioButtonV2 from "../customComponentsV2/common/radioButton/CustomRadioButtonV2";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AttributeUpdateDialog from "../../pages/labcar/Attributes/AttributeUpdateDialog";

const isNonEmptyString = (value) =>
  typeof value === "string" && value.trim().length > 0;


const hasValidItems = (items) =>
  Array.isArray(items) &&
  items.length >= 1 &&
  items.every(
    (item) =>
      isNonEmptyString(item?.title) && isNonEmptyString(item?.sub_title),
  );

const includesAllPositions = (items, selectedPositions) =>
  selectedPositions?.every((position) =>
    items?.some((item) => item?.position === position),
  );

const isSectionValid = (items, selectedPositions) =>
  hasValidItems(items) && includesAllPositions(items, selectedPositions);

const canSubmitDynamicForm = (
  dynamicFormFilteredData,
  selectedHeaders,
  selectedFooters,
  selectedSignatures,
) =>
  isSectionValid(dynamicFormFilteredData?.headers, selectedHeaders) &&
  isSectionValid(dynamicFormFilteredData?.footers, selectedFooters) &&
  isSectionValid(dynamicFormFilteredData?.signatures, selectedSignatures);

const buildUpdatedFields = (fields, currentField) =>
  fields?.map((field) =>
    field?.sequence === currentField?.sequence
      ? {
        ...field,
        field_name: currentField?.field_name,
        field_type: currentField?.field_type,
        edit_type: currentField?.edit_type,
      }
      : field,
  );

const buildNewFieldList = (fields, currentField) => [
  ...(fields || []),
  { ...currentField },
];
const textFieldStyle = {
  // 🔹 Match TextField border
  border: "1px solid #d9d9d9",
  borderRadius: 0,
  minHeight: 40,
  paddingLeft: "8px",

  "&:hover": {
    borderColor: "#8c8c8c",
  },

  "&.Mui-focused": {
    borderColor: "#000000",
  },

  // 🔹 Remove default underline (important)
  "&::before, &::after": {
    display: "none",
  },

  // 🔹 Selected text style
  "& .MuiSelect-select": {
    padding: "8px 32px 8px 8px",
    fontSize: "14px",
    color: "#000",
  },

  // 🔹 Dropdown arrow
  "& .MuiSelect-icon": {
    color: "#000",
    right: 6,
  },
};
const DynamicFormDialog = ({
  open,
  onClose,
  dynamicFormData,
  setDynamicFormData,
  dynamicFormFilteredData,
  setDynamicFormFilteredData,
  dialogTitle,
  onConfirm,
  onDisabled,
  selectedComponent,
}) => {
  /* States */
  const [selectedHeaders, setSelectedHeaders] = useState([]);
  const [selectedFooters, setSelectedFooters] = useState([]);
  const [selectedSignatures, setSelectedSignatures] = useState([]);
  const [openAttributeUpdateDialog, setOpenAttributeUpdateDialog] =
    useState(false);

  // Add/Update states
  const [isShowAddField, setIsShowAddField] = useState(true);
  const [showAddUpdateField, setShowAddUpdateField] = useState(false);
  const [currentField, setCurrentField] = useState({
    field_name: "",
    value: "",
    default_value: "",
    edit_type: "",
    // field_type: "",
    sequence: "",
  });

  const headerOptionsList = [
    { position: ALIGN_TYPES?.LEFT, label: "Left" },
    { position: ALIGN_TYPES?.CENTER, label: "Center" },
    { position: ALIGN_TYPES?.RIGHT, label: "Right" },
  ];

  const footerOptionsList = [
    { position: ALIGN_TYPES?.LEFT, label: "Left" },
    { position: ALIGN_TYPES?.CENTER, label: "Center" },
    { position: ALIGN_TYPES?.RIGHT, label: "Right" },
  ];

  const signatureOptionsList = [
    { position: ALIGN_TYPES?.LEFT, label: "Left" },
    { position: ALIGN_TYPES?.RIGHT, label: "Right" },
  ];

  /* Functions */
  const handleAddUpdateField = () => {
    const fields = isShowAddField
      ? buildNewFieldList(dynamicFormData?.fields, currentField)
      : buildUpdatedFields(dynamicFormData?.fields, currentField);

    setDynamicFormData({
      ...dynamicFormData,
      fields,
    });

    // Reset form and close it after adding or updating the field
    resetFieldInputs();
  };

  const handleFieldChange = (field, value) => {
    setCurrentField((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleEditField = (fieldSequence) => {
    // Find the field to edit by its sequence
    const fieldToEdit = dynamicFormData?.fields?.find(
      (field) => field?.sequence === fieldSequence,
    );

    // If the field is found, set it in the `currentField` state
    if (fieldToEdit) {
      setCurrentField(fieldToEdit);
      setShowAddUpdateField(true);
      setIsShowAddField(false);
    }
  };

  const handleDeleteField = (fieldSequence) => {
    // Filter out the field with the given sequence
    const updatedFields = dynamicFormData?.fields?.filter(
      (field) => field?.sequence !== fieldSequence,
    );

    // Update the dynamicFormData with the remaining fields
    setDynamicFormData((prevData) => ({
      ...prevData,
      fields: updatedFields,
    }));

    resetFieldInputs();
  };

  const getSelectedComponentType = () => {
    if (selectedComponent?.swm_id) return "swm";
    if (selectedComponent?.ee_id) return "ee";
    if (selectedComponent?.vc_id) return "vc";
    if (selectedComponent?.vs_id) return "vs";

    return "ee";
  };

  const resetFieldInputs = () => {
    // Reset the form fields
    setCurrentField({
      field_name: "",
      value: "",
      default_value: "",
      edit_type: "",
      // field_type: "",
      sequence: "",
    });

    // Close the "Add/Update Field" form
    setShowAddUpdateField(false);
  };

  /* UseEffects */
  useEffect(() => {
    // Define valid positions
    const validPositions = ["left", "center", "right"];
    const validPositions1 = ["left", "right"];

    // Update the selected headers list with valid positions
    let selectedHeadersList =
      dynamicFormData?.headers
        ?.map((el) =>
          validPositions?.includes(el?.position) ? String(el?.position) : null,
        )
        ?.filter(Boolean) || [];
    setSelectedHeaders(selectedHeadersList);

    // Update the selected signatures list with valid positions
    let selectedSignaturesList =
      dynamicFormData?.signatures
        ?.map((el) =>
          validPositions1?.includes(el?.position) ? String(el?.position) : null,
        )
        ?.filter(Boolean) || [];
    setSelectedSignatures(selectedSignaturesList);

    // Update the selected footers list with valid positions
    let selectedFootersList =
      dynamicFormData?.footers
        ?.map((el) =>
          validPositions?.includes(el?.position) ? String(el?.position) : null,
        )
        ?.filter(Boolean) || [];
    setSelectedFooters(selectedFootersList);
  }, [open]);

  useEffect(() => {
    setDynamicFormFilteredData({
      master_template_id: Number(dynamicFormData?.id),
      name: dynamicFormData?.name,
      type: dynamicFormData?.type,
      template_tag: "OLD",
      fields: dynamicFormData?.fields,
      headers: dynamicFormData?.headers?.filter((el) =>
        selectedHeaders?.includes(el?.position),
      ),
      footers: dynamicFormData?.footers?.filter((el) =>
        selectedFooters?.includes(el?.position),
      ),
      signatures: dynamicFormData?.signatures?.filter((el) =>
        selectedSignatures?.includes(el?.position),
      ),
      status: dynamicFormData?.status,
    });
  }, [open, dynamicFormData]);


  return (
    <Dialog
      open={open}
      // onClose={onClose}
      fullWidth
      maxWidth={"md"}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          textAlign: "center",
          boxShadow: 2,
          py: 1,
        }}
      >
        {/* Title text */}
        <Typography
          sx={{ flexGrow: 1, fontSize: fontSizeDetails.subtitle1.fontSize }}
        >
          {dialogTitle}
        </Typography>

        {/* Close Icon Button */}
        <IconButton
          edge="end"
          color="inherit"
          onClick={() => {
            onClose();
            resetFieldInputs();
          }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent
        dividers
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 4,
        }}
      >
        {/* Enter form name */}
        <Box>
          <CustomFormTextFieldV2
            required
            fullWidth
            size="small"
            label="Enter name of the form"
            placeholder=""
            type="text"
            fldType="text1" // 👈 minimum 1 character
            // variant="standard"
            value={dynamicFormData?.name ?? ""}
            setValue={(val) =>
              setDynamicFormData((prev) => ({
                ...prev,
                name: val,
              }))
            }
            txtFldStyle={
              {
                // TextField spacing or overrides if needed
              }
            }
          />
        </Box>

        {/* Form type */}
        <Box>
          <Typography
            variant="subtitle1"
            color={"grey"}
            component="label"
            htmlFor="form-type"
            sx={{ fontSize: fontSizeDetails.subtitle2.fontSize }}
          >
            Type of the form:{" "}
          </Typography>
          <Typography
            variant="subtitle1"
            color={"grey"}
            component="label"
            htmlFor="form-type"
            sx={{ fontSize: fontSizeDetails.subtitle2.fontSize }}
          >
            {dynamicFormData?.type === FORM_TYPES?.EE
              ? "EE Component"
              : dynamicFormData?.type === FORM_TYPES?.SWM
                ? "Software Module"
                : dynamicFormData?.type === FORM_TYPES?.VEHICLE
                  ? "Vehicle Component"
                  : ""}
          </Typography>
        </Box>

        {/* Select headers, footers & signature  */}
        <Box sx={{ display: "flex", gap: 4 }}>
          {/* Select headers  */}
          <Box sx={{ width: "100%" }}>
            <Typography
              variant="subtitle1"
              color={"grey"}
              component="label"
              htmlFor="form-type"
              sx={{ fontSize: fontSizeDetails.subtitle2.fontSize }}
            >
              Select header*
            </Typography>
            <FormControl variant="standard" fullWidth>
              {/* <InputLabel>Select header</InputLabel> */}
              <Select
                multiple
                variant="outlined"
                value={selectedHeaders}
                sx={textFieldStyle}
                onChange={(e) => setSelectedHeaders(e?.target?.value)}
                input={<Input />}
                renderValue={
                  (selected) =>
                    selected
                      ?.map(
                        (position) =>
                          headerOptionsList?.find(
                            (option) => option?.position === position,
                          )?.label,
                      )
                      ?.join(", ") // Display labels of selected values
                }
              >
                {headerOptionsList?.map((option) => (
                  <MenuItem key={option?.position} value={option?.position}>
                    <Checkbox
                      checked={selectedHeaders?.indexOf(option?.position) > -1}
                    />
                    <ListItemText primary={option?.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {/* Select footers  */}
          <Box sx={{ width: "100%" }}>
            <Typography
              variant="subtitle1"
              color={"grey"}
              component="label"
              htmlFor="form-type"
              sx={{ fontSize: fontSizeDetails.subtitle2.fontSize }}
            >
              Select footer*
            </Typography>
            <FormControl fullWidth>
              {/* <InputLabel>Select footer</InputLabel> */}
              <Select
                multiple
                value={selectedFooters}
                sx={textFieldStyle}
                onChange={(e) => setSelectedFooters(e?.target?.value)}
                input={<Input />}
                renderValue={
                  (selected) =>
                    selected
                      ?.map(
                        (value) =>
                          footerOptionsList?.find(
                            (option) => option?.position === value,
                          )?.label,
                      )
                      ?.join(", ") // Display labels of selected values
                }
              >
                {footerOptionsList?.map((option) => (
                  <MenuItem key={option?.position} value={option?.position}>
                    <Checkbox
                      checked={selectedFooters?.indexOf(option?.position) > -1}
                    />
                    <ListItemText primary={option?.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {/* Select signatures  */}
          <Box sx={{ width: "100%" }}>
            <Typography
              variant="subtitle1"
              color={"grey"}
              component="label"
              htmlFor="form-type"
              sx={{ fontSize: fontSizeDetails.subtitle2.fontSize }}
            >
              Select signature*
            </Typography>
            <FormControl variant="standard" fullWidth>
              {/* <InputLabel>Select signature</InputLabel> */}
              <Select
                multiple
                value={selectedSignatures}
                sx={textFieldStyle}
                onChange={(e) => setSelectedSignatures(e?.target?.value)}
                input={<Input />}
                renderValue={
                  (selected) =>
                    selected
                      ?.map(
                        (value) =>
                          signatureOptionsList?.find(
                            (option) => option?.position === value,
                          )?.label,
                      )
                      ?.join(", ") // Display labels of selected values
                }
              >
                {signatureOptionsList?.map((option) => (
                  <MenuItem key={option?.position} value={option?.position}>
                    <Checkbox
                      checked={
                        selectedSignatures?.indexOf(option?.position) > -1
                      }
                    />
                    <ListItemText primary={option?.label} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          {/* Add  */}
          <Box
            sx={{
              width: "70%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CustomButtonV2
              type="submit"
              size="small"
              btnName="Add"
              handleOnClick={() => {
                // Reset the form fields
                setCurrentField({
                  field_name: "",
                  value: "",
                  default_value: "",
                  edit_type: "",
                  // field_type: "",
                  sequence: "",
                });
                setShowAddUpdateField(true);
                setIsShowAddField(true);
              }}
              btnStyle={{
                ml: 2,
              }}
            />
            <IconButton
              onClick={() => {
                if (selectedComponent) {
                  setOpenAttributeUpdateDialog(true);
                }
              }}
              disabled={!selectedComponent}
              aria-label="copy attributes"
            >
              <VisibilityOutlinedIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Template & Add/Update field */}
        <Box sx={{ display: "flex", gap: 2.5 }}>
          {/* Template */}
          <DynamicFormTemplate
            handleEditField={handleEditField}
            handleDeleteField={handleDeleteField}
            headerOptionsList={headerOptionsList}
            signatureOptionsList={signatureOptionsList}
            footerOptionsList={footerOptionsList}
            selectedHeaders={selectedHeaders}
            selectedSignatures={selectedSignatures}
            selectedFooters={selectedFooters}
            dynamicFormData={dynamicFormData}
            setDynamicFormData={setDynamicFormData}
          />

          {/* Add/Update field */}
          {showAddUpdateField ? (
            <Paper
              elevation={2}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                px: 1,
                py: 1.4,
                border: "1px solid grey",
                borderRadius: 1,
                height: "375px",
                width: "300px",
              }}
            >
              {/* Title text */}
              <Typography
                sx={{ fontSize: "17px", fontWeight: 600, textAlign: "center" }}
              >
                {isShowAddField ? "Add Field" : "Update Field"}
              </Typography>
              <Divider sx={{ m: -1, p: 0, border: "1px solid lightgrey" }} />

              {/* Enter sequence no */}
              <Box>
                <Typography
                  variant="subtitle1"
                  color={"grey"}
                  component="label"
                  htmlFor="sequence-no"
                  sx={{ fontSize: fontSizeDetails.subtitle2.fontSize }}
                >
                  {isShowAddField ? "Enter" : ""} sequence no*
                </Typography>
                <CustomFormTextFieldV2
                  fullWidth
                  id="sequence-no"
                  fldType="text"
                  size="small"
                  value={currentField?.sequence || ""}
                  disabled={
                    dynamicFormData?.fields?.some(
                      (otherField) =>
                        otherField?.sequence === currentField?.sequence,
                    ) && !isShowAddField
                  }
                  valueError={
                    dynamicFormData?.fields?.some(
                      (otherField) =>
                        otherField?.sequence === currentField?.sequence,
                    ) && isShowAddField
                      ? "Sequence must be unique"
                      : ""
                  }
                  setValue={(value) => handleFieldChange("sequence", value)}
                  sx={{ width: "220px", mt: -1 }}
                />
              </Box>

              {/* Enter field name */}
              <Box>
                <Typography
                  variant="subtitle1"
                  color={"grey"}
                  component="label"
                  htmlFor="field-name"
                  sx={{ fontSize: fontSizeDetails.subtitle2.fontSize }}
                >
                  Enter field name*
                </Typography>
                <CustomFormTextFieldV2
                  fullWidth
                  id="field-name"
                  fldType="text"
                  size="small"
                  value={currentField?.field_name || ""}
                  setValue={(value) => handleFieldChange("field_name", value)}
                  sx={{ width: "220px", mt: -1 }}
                />
              </Box>

              {/* Select field type */}
              <Box>
                <FormControl>
                  <FormLabel
                    sx={{
                      fontSize: fontSizeDetails.subtitle2.fontSize,
                      color: "black",
                    }}
                  >
                    Select field type*
                  </FormLabel>

                  <Box sx={{ marginLeft: "1rem" }}>
                    <CustomRadioButtonV2
                      value={currentField?.field_type || FIELD_TYPES?.TEXT}
                      onChange={(e) =>
                        handleFieldChange("field_type", e.target?.value)
                      }
                      sx={{ mt: -1 }}
                      data={[
                        { value: FIELD_TYPES?.NUMBER, label: "Number" },
                        { value: FIELD_TYPES?.TEXT, label: "Text" },
                      ]}
                    />
                  </Box>
                </FormControl>
              </Box>

              {/* Select edit type */}
              <Box>
                <FormControl>
                  <FormLabel
                    sx={{
                      fontSize: fontSizeDetails.subtitle2.fontSize,
                      color: "black",
                    }}
                  >
                    Select Edit Type*
                  </FormLabel>

                  <Box sx={{ marginLeft: "1rem" }}>
                    <CustomRadioButtonV2
                      value={currentField?.edit_type || ""}
                      onChange={(e) =>
                        handleFieldChange("edit_type", e.target?.value)
                      }
                      data={[
                        { value: EDIT_TYPES?.DDH, label: "DDH" },
                        { value: EDIT_TYPES?.SNOW, label: "SNOW" },
                        { value: EDIT_TYPES?.BOTH, label: "BOTH" },
                      ]}
                      sx={{ mt: -1 }}
                    />
                  </Box>
                </FormControl>
              </Box>

              {/* Close & Add/Update buttons */}
              <Box
                sx={{ display: "flex", justifyContent: "flex-end", gap: 1.4 }}
              >
                <CustomButtonV2
                  size={"small"}
                  type="cancel"
                  variant={"contained"}
                  btnName={"Close"}
                  startIcon={<CloseIcon />}
                  handleOnClick={resetFieldInputs} // Reset the form
                  btnStyle={{}}
                />
                <CustomButtonV2
                  size={"small"}
                  variant={"contained"}
                  type={"submit"}
                  btnName={isShowAddField ? "Add" : "Update"}
                  disabled={
                    !(
                      currentField?.field_name?.length &&
                      String(currentField?.edit_type)?.length &&
                      String(currentField?.sequence)?.length
                    ) ||
                    (isShowAddField &&
                      dynamicFormData?.fields?.some(
                        (otherField) =>
                          otherField?.sequence === currentField?.sequence,
                      ))
                  }
                  startIcon={isShowAddField ? <CheckIcon /> : <UpdateIcon />}
                  handleOnClick={handleAddUpdateField}
                  btnStyle={{}}
                />
              </Box>
            </Paper>
          ) : null}
        </Box>
      </DialogContent>
      <DialogActions sx={{ boxShadow: 4, gap: 2, px: 2, py: 1 }}>
        <CustomButtonV2
          type="cancel"
          size="small"
          btnName="Close"
          handleOnClick={() => {
            onClose();
            resetFieldInputs();
          }}
        />
        <CustomButtonV2
          size={"small"}
          variant={"contained"}
          type="submit"
          btnName={"Add"}
          // startIcon={<CheckIcon />}
          disabled={
            !canSubmitDynamicForm(
              dynamicFormFilteredData,
              selectedHeaders,
              selectedFooters,
              selectedSignatures,
            )
          }
          handleOnClick={() => {
            onClose();
            resetFieldInputs();
            onConfirm();
          }}
          btnStyle={{}}
        />
      </DialogActions>

      <AttributeUpdateDialog
        openAttributeUpdateDialog={openAttributeUpdateDialog}
        setOpenAttributeUpdateDialog={setOpenAttributeUpdateDialog}
        handleCloseAttributeUpdateDialog={() => {
          setOpenAttributeUpdateDialog(false);
        }}
        onClickSuccess={() => {
          setOpenAttributeUpdateDialog(false);
        }}
        selectedComponent={selectedComponent}
        componentType={getSelectedComponentType()}
        type="COPY"
        hideDefaultActions
      />
    </Dialog>
  );
};

export default DynamicFormDialog;
